/**
 * Utility functions for handling timestamps and timezone conversion
 * Supports both ISO string timestamps and Unix epoch timestamps
 */

/**
 * Converts a timestamp (ISO string, epoch number, or Date object) to local timezone and formats it
 * @param timestamp - ISO string, Unix epoch timestamp (seconds), or Date object
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted time string in local timezone
 */
export const formatTimestamp = (
  timestamp: string | number | Date,
  options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }
): string => {
  try {
    let date: Date;
    
    if (typeof timestamp === 'number') {
      // Handle Unix epoch timestamp (seconds)
      date = new Date(timestamp * 1000); // Convert to milliseconds
    } else if (typeof timestamp === 'string') {
      // Handle ISO string timestamp
      date = new Date(timestamp);
    } else {
      // Handle Date object
      date = timestamp;
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid timestamp:', timestamp);
      return 'Invalid time';
    }
    
    // Format in local timezone
    return date.toLocaleTimeString('en-US', options);
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Invalid time';
  }
};

/**
 * Formats a timestamp for display with date and time
 * @param timestamp - ISO string, Unix epoch timestamp (seconds), or Date object
 * @returns Formatted date and time string
 */
export const formatDateTime = (timestamp: string | number | Date): string => {
  try {
    let date: Date;
    
    if (typeof timestamp === 'number') {
      // Handle Unix epoch timestamp (seconds)
      date = new Date(timestamp * 1000); // Convert to milliseconds
    } else if (typeof timestamp === 'string') {
      // Handle ISO string timestamp
      date = new Date(timestamp);
    } else {
      // Handle Date object
      date = timestamp;
    }
    
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    // Check if it's today
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return `Today at ${date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })}`;
    }
    
    // Check if it's yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();
    
    if (isYesterday) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })}`;
    }
    
    // For other dates, show the full date and time
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.error('Error formatting date time:', error);
    return 'Invalid date';
  }
};

/**
 * Converts UTC timestamp to local timezone Date object
 * @param utcTimestamp - UTC timestamp string or Unix epoch timestamp (seconds)
 * @returns Date object in local timezone
 */
export const utcToLocal = (utcTimestamp: string | number): Date => {
  try {
    if (typeof utcTimestamp === 'number') {
      // Handle Unix epoch timestamp (seconds)
      return new Date(utcTimestamp * 1000); // Convert to milliseconds
    } else {
      // Handle ISO string timestamp
      return new Date(utcTimestamp);
    }
  } catch (error) {
    console.error('Error converting UTC to local:', error);
    return new Date();
  }
};

/**
 * Gets relative time string (e.g., "2 minutes ago", "1 hour ago")
 * @param timestamp - ISO string, Unix epoch timestamp (seconds), or Date object
 * @returns Relative time string
 */
export const getRelativeTime = (timestamp: string | number | Date): string => {
  try {
    let date: Date;
    
    if (typeof timestamp === 'number') {
      // Handle Unix epoch timestamp (seconds)
      date = new Date(timestamp * 1000); // Convert to milliseconds
    } else if (typeof timestamp === 'string') {
      // Handle ISO string timestamp
      date = new Date(timestamp);
    } else {
      // Handle Date object
      date = timestamp;
    }
    
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }
    
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    
    // If months is 0, show weeks instead to avoid "0 month ago"
    if (diffInMonths === 0) {
      return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
    }
    
    if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
    }
    
    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
  } catch (error) {
    console.error('Error getting relative time:', error);
    return 'Unknown time';
  }
};

/**
 * Helper function to pad numbers with leading zeros
 */
const padZero = (num: number): string => {
  return num < 10 ? `0${num}` : `${num}`;
};

/**
 * Generates a thread title with current local time
 * @returns Thread title in format "YYYY-MM-DD HH:MM:SS" in user's local timezone
 */
export const generateThreadTitle = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = padZero(now.getMonth() + 1);
  const day = padZero(now.getDate());
  const hours = padZero(now.getHours());
  const minutes = padZero(now.getMinutes());
  const seconds = padZero(now.getSeconds());
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Returns the thread title as-is without any conversion
 * @param title - Thread title string
 * @returns The title string unchanged
 */
export const formatThreadTitle = (title: string): string => {
  // Remove "Req: " prefix if it exists (legacy requirement group threads)
  if (title.startsWith('Req: ')) {
    return title.substring(5);
  }
  return title;
};

/**
 * Converts epoch timestamp to JavaScript Date object
 * @param epochTimestamp - Unix epoch timestamp in seconds
 * @returns Date object
 */
export const epochToDate = (epochTimestamp: number): Date => {
  return new Date(epochTimestamp * 1000); // Convert seconds to milliseconds
};

/**
 * Converts Date object to epoch timestamp
 * @param date - Date object
 * @returns Unix epoch timestamp in seconds
 */
export const dateToEpoch = (date: Date): number => {
  return Math.floor(date.getTime() / 1000); // Convert milliseconds to seconds
};

export const formatDate = (dateInput: string | number): string => {
  // Handle both Unix timestamp (seconds) and ISO date strings
  // ISO format example: "2025-12-03T12:07:49.207000"
  let date: Date;
  
  if (typeof dateInput === 'number') {
    // Unix timestamp in seconds - convert to milliseconds
    date = new Date(dateInput * 1000);
  } else if (typeof dateInput === 'string') {
    // Check if it's a numeric string (Unix timestamp)
    const numericValue = parseFloat(dateInput);
    if (!isNaN(numericValue) && dateInput.match(/^\d+\.?\d*$/)) {
      // It's a Unix timestamp as string
      date = new Date(numericValue * 1000);
    } else {
      // It's an ISO date string or other date format
      // If the string doesn't have timezone info (Z or +/-), treat it as UTC
      let dateString = dateInput;
      if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/) && !dateString.match(/Z|[+-]\d{2}:\d{2}$/)) {
        // Add 'Z' to indicate UTC timezone
        dateString = dateString.replace(/(\.\d+)?$/, '') + 'Z';
      }
      date = new Date(dateString);
    }
  } else {
    date = new Date();
  }
  
  // Validate date
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }
  
  // Get current time in browser's timezone
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  // Yesterday
  if (diffDays === 1) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `Yesterday at ${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  }
  
  // Today
  if (diffDays === 0 && diffMs >= 0) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `Today at ${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  }
  
  // This week (within 7 days)
  if (diffDays < 7 && diffDays >= 0) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${days[date.getDay()]} at ${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  }
  
  // Older dates - show in browser's local timezone
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

